import {
  IconCalculator,
  IconChartLine,
  IconChecklist,
  IconEdit,
  IconFileInvoice,
  IconGauge,
  IconHistory,
  IconLayoutGrid,
  IconList,
  IconPhoneOff,
  IconPresentationAnalytics,
  IconReportMoney,
  IconSearch,
  IconSignalE,
  IconTable,
  IconUser,
} from '@tabler/icons-react';
import { RouteUtils } from '@bobex/react-core/routes/RouteUtils';
import { UserGroupColorUtils } from '@bobex/react-core/utils/UserGroupColorUtils';
import { RecentAbstractView } from '@bobex/react-core/domain/RecentModel/view/RecentAbstractView';
import { RecentMemberView } from '@bobex/react-core/domain/RecentModel/view/RecentMemberView';
import { RecentAbstractUtil } from '@bobex/react-core/domain/RecentModel/util/RecentAbstractUtil';
import { BoNavigationGroup } from '@bobex/react-core/types/NavigationGroup';
import { PartnerRfpUtils } from '@bobex/react-core/domain/PartnerRfpModel/PartnerRfpUtils';

export const BoNavigationItems = (
  languageId: number,
  t: Function,
  recentHistory?: RecentAbstractView[]
): BoNavigationGroup[] => {
  const shouldShowFrLinks = RouteUtils.shouldShowFrLinks();
  const isNlContext = RouteUtils.getContext() === 'nl';
  // PARTNER_RFP_NEW_PAGES: the lead lists open the new React pages instead of the legacy ones
  const usesNewLeadPages = PartnerRfpUtils.usesNewPages();
  const leadsLink = (reactLink: string, legacyPath: string) =>
    usesNewLeadPages ? reactLink : RouteUtils.getBackofficeServletBasename() + legacyPath;

  return [
    {
      label: t('Sales'),
      icon: IconReportMoney,
      authority: ['CAN_LOGIN'],
      links: [
        {
          label: t('My prospects'),
          link: '/my-prospects',
          authority: ['CAN_LOGIN'],
          counters: ['assigned-sales.total'],
        },
        {
          label: t('My suppliers'),
          link: '/my-suppliers',
          authority: ['CAN_LOGIN'],
        },
        {
          label: t('Supplier relaunch'),
          link: '/supplier-relaunch',
          authority: ['READ_SUPPLIER_RELAUNCH'],
        },
        {
          label: t('search.prospects'),
          link: '/search-assignation-sales',
          authority: ['CAN_LOGIN'],
        },
        {
          label: t('search.customers'),
          link: '/search-customers',
          authority: ['CAN_LOGIN'],
        },
      ],
    },

    {
      label: isNlContext ? t('Premium leads NL') : t('Premium leads'),
      icon: IconChecklist,
      initiallyOpened: true,
      authority: ['CAN_LOGIN'],
      links: [
        ...(shouldShowFrLinks
          ? [
              {
                label: `FR Leads`,
                link: leadsLink('/leads-list/fr', '/withheld_partner_rfps?language.id=5'),
                authority: ['CAN_LOGIN'],
                counters: ['withheld-partner-rfp.fr', 'withheld-no-call-partner-rfp.not-today.fr'],
                title: 'Open leads / Never called',
                subtitle: 'Today: {withheld-no-call-partner-rfp.today.fr}',
                subtitleIcon: IconPhoneOff,
              },
            ]
          : []),
        {
          label: 'NL Leads',
          link: leadsLink('/leads-list/nl', '/withheld_partner_rfps?language.id=2'),
          authority: ['CAN_LOGIN'],
          counters: ['withheld-partner-rfp.nl', 'withheld-no-call-partner-rfp.not-today.nl'],
          title: 'Open leads / Never called',
          subtitle: 'Today: {withheld-no-call-partner-rfp.today.nl}',
          subtitleIcon: IconPhoneOff,
        },
        ...(shouldShowFrLinks
          ? [
              {
                label: t('No suppliers FR'),
                link: leadsLink('/leads-list/fr/no-suppliers', '/withheld_partner_rfps?language.id=5&cannot_contact=2'),
                authority: ['CAN_LOGIN'],
                counters: ['no-supplier-partner-rfp.fr'],
              },
            ]
          : []),
        {
          label: t('No suppliers NL'),
          link: leadsLink('/leads-list/nl/no-suppliers', '/withheld_partner_rfps?language.id=2&cannot_contact=2'),
          authority: ['CAN_LOGIN'],
          counters: ['no-supplier-partner-rfp.nl'],
        },
        ...(shouldShowFrLinks
          ? [
              {
                label: t('After 17h FR'),
                link: leadsLink('/leads-list/fr/after-17h', '/withheld_partner_rfps?language.id=5&cannot_contact=3'),
                authority: ['CAN_LOGIN'],
                counters: ['after-17-h-partner-rfp.fr'],
              },
            ]
          : []),
        {
          label: t('After 17h NL'),
          link: leadsLink('/leads-list/nl/after-17h', '/withheld_partner_rfps?language.id=2&cannot_contact=3'),
          authority: ['CAN_LOGIN'],
          counters: ['after-17-h-partner-rfp.nl'],
        },
        ...(shouldShowFrLinks
          ? [
              {
                label: 'Lead+ & NSL FR',
                link: leadsLink('/leads-list/fr/suppliers', '/withheld_partner_rfps?language.id=5&leadsplus=1'),
                authority: ['CAN_LOGIN'],
                counters: ['lead-plus.fr'],
              },
            ]
          : []),
        {
          label: 'Lead+ & NSL NL',
          link: leadsLink('/leads-list/nl/suppliers', '/withheld_partner_rfps?language.id=2&leadsplus=1'),
          authority: ['CAN_LOGIN'],
          counters: ['lead-plus.nl'],
        },
        {
          label: 'Callback',
          link:
            RouteUtils.getBackofficeServletBasename() +
            '/list_users_for_callback?language.id=' +
            languageId,
          authority: ['CAN_LOGIN'],
        },
        {
          label: t('External Leads'),
          link: '/external-leads-in-process',
          authority: ['EXTERNAL_LEAD_VIEW'],
          counters: ['external-leads-in-process'],
        },
      ],
    },
    {
      label: t('History'),
      icon: IconHistory,
      authority: ['CAN_LOGIN'],
      links: recentHistory
        ? recentHistory.map((item: RecentAbstractView) => {
            return {
              label: RecentAbstractUtil.getRecentItemDisplayLabel(item),
              link: RecentAbstractUtil.getRecentItemNavigationLink(item),
              authority: ['CAN_LOGIN'],
              icon: item.type === 2 ? IconUser : IconEdit,
              iconColor:
                item instanceof RecentMemberView
                  ? UserGroupColorUtils.getUserGroupColor(item.memberUserGroupId)
                  : '',
            };
          })
        : [],
    },
    {
      label: isNlContext ? t('Search NL') : t('Search BE'),
      icon: IconSearch,
      authority: ['CAN_LOGIN'],
      links: [
        {
          label: t('sitemap.bo_user_searchform'),
          link: '/search-user',
          authority: ['CAN_LOGIN'],
          icon: IconUser,
        },
        {
          label: t('sitemap.prospect_searchform'),
          link: RouteUtils.getBackofficeServletBasename() + '/prospect_searchform',
          authority: ['CAN_LOGIN'],
          icon: IconUser,
        },
        {
          label: t('sitemap.bo_partner_rfp_searchform'),
          link: '/partner-rfp-search-form',
          authority: ['CAN_LOGIN'],
          icon: IconEdit,
        },
      ],
    },
    {
      label: t('Invoicing'),
      icon: IconFileInvoice,
      authority: ['CAN_LOGIN'],
      links: [
        {
          label: t('sitemap.bo_invoice_searchform'),
          link: '/search-invoice',
          authority: ['CAN_LOGIN'],
          icon: IconCalculator,
        },
        {
          label: t('Free lead invoicing'),
          link: '/free-lead-invoicing',
          authority: ['MANAGE_FREE_LEAD_INVOICING'],
          icon: IconReportMoney,
        },
        {
          label: t('Twikey mandate export'),
          link: '/twikey-mandate-export',
          authority: ['MANAGE_TWIKEY_EXPORT'],
          icon: IconReportMoney,
        },
      ],
    },
    {
      label: t('Dashboard'),
      icon: IconGauge,
      authority: ['CAN_LOGIN'],
      links: [
        {
          label: 'Dashboard+GM',
          link: '/dashboard?gm=1',
          authority: ['CAN_LOGIN'],
          icon: IconGauge,
        },
        {
              label: t('Incentives Dashboard'),
              link: '/incentives',
              authority: ['VIEW_INCENTIVE_DASHBOARD'],
              icon: IconGauge,
        },
        {
          label: t('Dashboard prospects'),
          link: '/gsupplier-marketing',
          authority: ['VIEW_CHARTS_PLUS', 'VIEW_SPM_DASHBOARD'],
          icon: IconGauge,
        },
          {
              label: t('Working dashboard'),
              link: '/sales-work-dashboard',
              authority: ['READ_OPPORTUNITIES'],
              icon: IconGauge,
          },
          {
          label: t('Menu.current_month_sales'),
          link: '/sales-current-dashboard',
          authority: ['CAN_LOGIN'],
          icon: IconGauge,
        },
        {
          label: t('Menu.opportunities_pipeline'),
          link: '/sales-opportunities-dashboard',
          authority: ['CAN_LOGIN'],
          icon: IconGauge,
        },
        {
          label: t('Menu.sales_dashboard'),
          link: '/sales-vendor-dashboard',
          authority: ['READ_OPPORTUNITIES', 'VIEW_ALL_OPPORTUNITIES'],
          icon: IconGauge,
        },
        {
          // Migrated (REBO-802): now the "global" tab of the sales-vendor dashboard. Narrowed from
          // CAN_LOGIN to the tab's own authority — the entry used to be visible to every logged-in
          // user for a page exposing the whole company's revenue.
          label: t('Menu.global_customer_dashboard'),
          link: '/sales-vendor-dashboard?tab=global',
          authority: ['VIEW_ALL_OPPORTUNITIES'],
          icon: IconGauge,
        },
        {
          label: t('Menu.payments'),
          link: '/payments-dashboard',
          authority: ['VIEW_CHARTS_ADMIN'],
          icon: IconGauge,
        },
        {
          label: t('Menu.dashboard_lawyer'),
          link: '/lawyer-dashboard',
          authority: ['READ_LAWYER_DASHBOARD'],
          icon: IconGauge,
        },
      ],
    },
      {
          label: t('Charts'),
          icon: IconPresentationAnalytics,
          authority: ['CAN_LOGIN'],
          links: [
              {
                  label: t('Menu.revenue_per_client'),
                  link: '/revenues-per-premium-partner',
                  authority: ['VIEW_CHARTS_ADMIN', 'VIEW_CHARTS_PLUS'],
                  icon: IconSignalE,
              },
              {
                  label: t('Menu.premium_leads_monthly'),
                  link: '/monthly-global-rfps?tab=LEADS&granularity=MONTH',
                  authority: ['VIEW_CHARTS_ADMIN', 'VIEW_CHARTS_PLUS'],
                  icon: IconChartLine,
              },
              {
                  label: t('Menu.mkt_channels_monthly_be'),
                  link: '/affiliate-group-rfp-statistics?viewMode=affGroup&langue=all&period=month&time=2years&dateBasis=submission&statuses=12&tab=stacked-chart',
                  authority: ['VIEW_CHARTS'],
                  icon: IconSignalE,
              },
              {
                  label: t('Menu.lr_revenue_by_category'),
                  link: '/premium-category-topyearly?excluderel=true&dateMode=submission',
                  authority: ['VIEW_CHARTS_ADMIN', 'VIEW_CHARTS_PLUS'],
                  icon: IconTable,
              },
              {
                  label: t('Menu.revenues_monthly_be'),
                  link: '/monthly-targets?scope=local',
                  authority: ['VIEW_CHARTS_PLUS'],
                  icon: IconTable,
              },
              {
                  label: 'Top 20',
                  link:
                      RouteUtils.getBackofficeServletBasename() +
                      '/gchart_pppleads_partner?startdate=20250401&enddate=20250430&select=202504&beci=y&type=top20',
                  authority: ['CAN_LOGIN'],
                  icon: IconList,
              },
          ],
      },
      {
            label: 'Admin',
            icon: IconLayoutGrid,
            authority: ["CAN_LOGIN"],
            links: [
                {
                    label: t('Menu.zipDistance', 'Codes postaux en vicinité'),
                    link: '/zip-distance',
                    authority: ["CAN_LOGIN"],
                    icon: IconCalculator
                },
                {
                    label: t('Menu.lead.simul'),
                    link: '/lead-simulation',
                    authority: ["CAN_LOGIN"],
                    icon: IconCalculator,
                },
                {
                    label: t('Menu.leadprice.simul'),
                    link: '/lead-price-simulation',
                    authority: ["CAN_LOGIN"],
                    icon: IconCalculator,
                },
                {
                    label: t("Lawyer Tool"),
                    link: "/lawyer-tool",
                    authority: ["READ_LAWYER"],
                },
                {
                    label: t("Menu.hard_to_reach_fr"),
                    link: RouteUtils.getBackofficeServletBasename() + '/withheld_partner_rfps?language.id=5&cannot_contact=1&action=partnerrfp.fr.cannotContact',
                    authority: ["CAN_LOGIN"],
                    counters: ["hard-to-reach-fr"]
                },
                {
                    label: t("Menu.hard_to_reach_nl"),
                    link: RouteUtils.getBackofficeServletBasename() + '/withheld_partner_rfps?language.id=2&cannot_contact=1&action=partnerrfp.nl.cannotContact',
                    authority: ["CAN_LOGIN"],
                    counters: ["hard-to-reach-nl"]
                },
                {
                    label: t("Business Max Selection"),
                    link: "/business-max-selection",
                    authority: ["READ_BUSINESS_MAX_SELECTION"],
                },
                {
                    label: t("Related Business Types"),
                    link: "/related-bus-types",
                    authority: ["READ_RELATED_BUS_TYPE"],
                },
            ],
        },
    ];
};
